import { inject, Injectable } from '@angular/core';
import { KitDetailsComponent } from '@app/modules/kits/components/kit-details/kit-details.component';
import { Kit } from '@core/core.types';
import { KitsFilters } from '@core/services/kits.service';
import { Logger } from '@core/services/logger.service';
import { LoadKitById, LoadKitsByIdAction } from '@core/store/kits/kits.actions';
import { KitsState } from '@core/store/kits/kits.state';
import { HttpUtils } from '@core/utils/http-utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { GetFollowUpsListAction } from '../follow-ups/store/follow-ups.actions';
import { FollowUpsState } from '../follow-ups/store/follow-ups.state';
import { ExtendedKit, KitExtensions } from './kits.types';

@Injectable({
  providedIn: 'root',
})
export class KitsUtilsService {
  private store = inject(Store);
  private ngbModal = inject(NgbModal);

  private readonly log = new Logger(this.constructor.name);

  getKitById$(kitId: string, refresh = false): Observable<Kit> {
    this.log.debug('getKitById$');
    if (!kitId) {
      return of(null);
    }
    return this.store.dispatch(new LoadKitById(kitId, refresh)).pipe(
      switchMap(() =>
        this.store.select(KitsState.getKitById).pipe(
          map((filterById: (id: string) => Kit) => filterById(kitId)),
          filter((k) => k !== undefined)
        )
      ),
      distinctUntilChanged()
    );
  }

  getKitsById$(kitIds: string[], refresh = false): Observable<Kit[]> {
    if (!kitIds.length) {
      return of(null);
    }
    return this.store.dispatch(new LoadKitsByIdAction(kitIds, refresh)).pipe(
      switchMap(() =>
        this.store.select(KitsState.getKitsByIds).pipe(
          map((filterByIds: (ids: string[]) => Kit[]) => filterByIds(kitIds)),
          filter((k) => k !== undefined)
        )
      )
    );
  }

  openKitDetails(kitId) {
    const modalRef = this.ngbModal.open(KitDetailsComponent, {
      centered: true,
      modalDialogClass: 'modal-size-xxl',
    });
    modalRef.componentInstance.kitId = kitId;
  }

  buildHttpParams(params: KitsFilters) {
    let httpParams = HttpUtils.buildHttpParams(params);

    if (params.productInventoryItemIds?.length) {
      httpParams = httpParams.set(
        'productInventoryItemIds',
        params.productInventoryItemIds.join(',')
      );
    }

    return httpParams;
  }

  public getExtendedKit(
    kit: Kit,
    appends: KitExtensions = { followUps: false }
  ): Observable<ExtendedKit> {
    if (!kit) {
      return of(null);
    }

    return forkJoin([
      appends.followUps
        ? this.store.dispatch(
            new GetFollowUpsListAction(
              { ids: kit.followUpIds ?? [] },
              {},
              null,
              null
            )
          )
        : of(null),
    ]).pipe(
      switchMap(() =>
        combineLatest([
          of(kit),
          this.store
            .select(FollowUpsState.getFollowUpsByIds)
            .pipe(
              map((getFollowUpsByIds) => getFollowUpsByIds(kit?.followUpIds))
            ),
        ])
      ),
      map(([kit, followUps]) => ({
        ...kit,
        followUps,
      }))
    );
  }

  public loadKitConnectedData(kitList: Kit[], appends: KitExtensions) {
    if (!kitList.length) {
      return of([]);
    }

    const followUpIds: string[] = [];
    kitList.forEach((kit) => {
      followUpIds.push(...(kit?.followUpIds ?? []));
    });

    return forkJoin([
      appends.followUps
        ? this.store.dispatch(
            new GetFollowUpsListAction({ ids: followUpIds }, {}, null, null)
          )
        : of(null),
    ]);
  }
}
